import { User, useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { datadogRum } from "@datadog/browser-rum";
import Intercom, { shutdown as intercomShutdown } from "@intercom/messenger-js-sdk";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef } from "react";

import { useGetIntercomUserJwtQuery } from "@api/graphql/types-and-hooks";
import { OnboardingRedirectGate } from "@pages/Onboarding/OnboardingRedirectGate";
import { useAdminData } from "@providers/AdminDataProvider";
import { BackgroundSyncProvider } from "@providers/BackgroundSyncProvider";

import { IndexLayout } from "./layouts";

/**
 * App will handle all inbound traffic. It starts by checking if Auth0 is loading in which case it render a loader.
 * Otherwise it will do one of three things
 *  - if a user is logged in, it will redirect them to the first page of the dashboard
 * @returns
 */
function App() {
  const { user, isAuthenticated } = useAuth0();
  const intercomBooted = useRef(false);
  const rolesKey = `${config.auth0Audience}/roles`;
  const rolesString = user?.[rolesKey];
  const { "fin-ai-messenger": finAiMessengerEnabled } = useFlags<FeatureFlags>();
  const { isUserCaregiver, roles } = useAdminData();
  const { data: intercomJwtData, isError: intercomJwtError } = useGetIntercomUserJwtQuery(
    {},
    {
      enabled: isAuthenticated && roles.length > 0 && !!finAiMessengerEnabled && !isUserCaregiver,
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: 7 * 60 * 60 * 1000, // refresh at 7h — backend JWT expires at 8h
      queryKey: ["getIntercomUserJwt", user?.sub],
    },
  );
  const intercomUserJwt = intercomJwtData?.getLoginProvider?.intercomUserJwt ?? undefined;

  const initializePendo = (user: User) => {
    if (window.pendo) {
      window.pendo.initialize({
        visitor: {
          id: user?.sub,
          email: user?.email,
          full_name: `${user?.given_name} ${user?.family_name}`,
          roles: rolesString.length ? rolesString : "",
        },
        account: {
          id: user?.org_id,
          orgName: user?.[`${config.auth0Audience}/orgName`],
        },
      });
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      const orgName = `${user![config.auth0Audience + "/orgName"] ?? ""}`;
      datadogRum.setUser({
        email: user?.email,
        id: user?.sub,
        name: user?.name,
        orgId: user?.org_id || "<missing-org-id>",
        orgName: orgName,
      });

      initializePendo(user);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user && intercomUserJwt && !intercomJwtError && finAiMessengerEnabled && !isUserCaregiver) {
      try {
        Intercom({
          app_id: "hfa7cbkg",
          user_id: user?.sub,
          name: user?.name,
          email: user?.email,
          intercom_user_jwt: intercomUserJwt,
          company: {
            id: user?.org_id,
            name: user?.[`${config.auth0Audience}/orgName`],
          },
        });
        intercomBooted.current = true;
      } catch (e) {
        datadogRum.addError(e, { source: "intercom-init" });
      }
    } else if (intercomBooted.current) {
      try {
        intercomShutdown();
      } catch (e) {
        datadogRum.addError(e, { source: "intercom-shutdown" });
      }
      intercomBooted.current = false;
    }
  }, [isAuthenticated, user, intercomUserJwt, intercomJwtError, finAiMessengerEnabled, isUserCaregiver]);

  return (
    <BackgroundSyncProvider>
      <OnboardingRedirectGate>
        <IndexLayout />
      </OnboardingRedirectGate>
    </BackgroundSyncProvider>
  );
}

export default App;
